import { useCallback } from 'react'
import { ErrorMessages } from '@/components/FormErrors/ErrorMessages'
import { InputStartDay } from '@/components/InputDay/InputStartDay'
import { isUserError } from '@/services/apollo/userErrorLink'
import {
  ExternalInsuranceCancellationOption,
  type ProductOfferFragment,
  useCancellationRequestedUpdateMutation,
  useStartDateUpdateMutation,
} from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { convertToDate, formatAPIDate } from '@/utils/date'
import { useErrorMessage } from '@/utils/useErrorMessage'
import { useOptimisticValue } from '@/utils/useOptimisticValue'
import { IEXCancellation } from './IEXCancellation'

type Props = {
  productOfferIds: Array<string>
  offer: ProductOfferFragment
  onStartDateChange?: () => void
  onAutoSwitchChange?: (checked: boolean) => void
}

export const CancellationForm = ({ productOfferIds, offer, onStartDateChange, onAutoSwitchChange }: Props) => {
  const [isChecked, setIsChecked, revertToOriginalChecked] = useOptimisticValue(offer.cancellation.requested)

  const [updateSwitchingRequest, updateSwitchingResult] = useCancellationRequestedUpdateMutation()
  const [updateStartDate, updateStartDateResult] = useStartDateUpdateMutation()

  const handleAutoSwitchChange = (checked: boolean) => {
    setIsChecked(!isChecked)

    updateSwitchingRequest({
      variables: { productOfferIds, requested: checked },
      onError: () => revertToOriginalChecked(),
      onCompleted: () => onAutoSwitchChange?.(checked),
    })
  }

  const handleChangeStartDate = useCallback(
    (date: Date) => {
      updateStartDate({
        variables: {
          productOfferIds,
          startDate: formatAPIDate(date),
        },
        onCompleted: () => {
          onStartDateChange?.()
        },
        onError: (error) => {
          // A refused start date is expected user input, not a system fault - only log real failures.
          if (!isUserError(error)) {
            logger.error('Failed to update start date', { error })
          }
        },
      })
    },
    [productOfferIds, updateStartDate, onStartDateChange],
  )

  const startDate = convertToDate(offer.startDate) ?? undefined
  const minStartDate = convertToDate(offer.minStartDate) ?? undefined
  const maxStartDate = convertToDate(offer.maxStartDate) ?? undefined
  const startDateErrorMessage = useErrorMessage(updateStartDateResult.error)
  const startDateErrors = startDateErrorMessage ? [startDateErrorMessage] : undefined
  const switcherCompanyName = offer.cancellation.externalInsurer?.displayName

  switch (offer.cancellation.option) {
    case ExternalInsuranceCancellationOption.None: {
      return (
        <>
          <InputStartDay
            date={startDate}
            minDate={minStartDate}
            maxDate={maxStartDate}
            onChange={handleChangeStartDate}
            loading={updateStartDateResult.loading}
          />
          <ErrorMessages errors={startDateErrors} />
        </>
      )
    }
    case ExternalInsuranceCancellationOption.Iex: {
      return (
        <>
          <IEXCancellation
            requested={isChecked}
            companyName={switcherCompanyName ?? 'Unknown'}
            startDate={startDate}
            minStartDate={minStartDate}
            maxStartDate={maxStartDate}
            loading={updateStartDateResult.loading || updateSwitchingResult.loading}
            onAutoSwitchChange={handleAutoSwitchChange}
            onStartDateChange={handleChangeStartDate}
          />
          <ErrorMessages errors={startDateErrors} />
        </>
      )
    }
  }
}
